import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPricing() {
  const { logout } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teachingNoteRequests, setTeachingNoteRequests] = useState([]);

  useEffect(() => {
    fetchPricing();
    fetchTeachingNoteRequests();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing(response.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    }
  };

  const fetchTeachingNoteRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/teaching-note-requests`);
      setTeachingNoteRequests(response.data);
    } catch (error) {
      toast.error('Failed to load teaching note requests');
    }
  };

  const handleUpdatePricing = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/admin/pricing`, pricing);
      toast.success('Pricing updated successfully');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
              The Case Verse - Admin
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/admin" className="text-slate-700 hover:text-slate-900" data-testid="nav-dashboard-link">Dashboard</Link>
              <Link to="/admin/pricing" className="text-slate-900 font-medium" data-testid="nav-pricing-link">Pricing & Settings</Link>
              <Link to="/cases" className="text-slate-700 hover:text-slate-900" data-testid="nav-cases-link">Case Library</Link>
              <Link to="/book-training" className="text-slate-700 hover:text-slate-900" data-testid="nav-book-training-link">Book Training</Link>
              <Link to="/request-case" className="text-slate-700 hover:text-slate-900" data-testid="nav-request-case-link">Request Case</Link>
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="logout-button">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-8" data-testid="page-title">Pricing & Settings</h1>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <div className="bg-white p-8 rounded border border-slate-200 mb-8">
                <h2 className="text-2xl font-serif font-bold mb-2">Academic Per-ID Pricing</h2>
                <p className="text-sm text-slate-500 mb-6">Academic institutes pay per access ID (student or faculty). Corporate pricing is managed on the Admin Dashboard → Pages tab.</p>
                {!pricing ? (
                  <p className="text-slate-500 text-sm">Loading…</p>
                ) : (
                <form onSubmit={handleUpdatePricing} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      India — price per ID (₹)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={pricing.academic_inr_per_id ?? 500}
                      onChange={(e) => setPricing({...pricing, academic_inr_per_id: parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="academic-inr-per-id-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      International — price per ID ($)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={pricing.academic_usd_per_id ?? 10}
                      onChange={(e) => setPricing({...pricing, academic_usd_per_id: parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="academic-usd-per-id-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    data-testid="save-pricing-button"
                  >
                    {loading ? 'Saving...' : 'Save Pricing'}
                  </button>
                </form>
                )}
              </div>
            </div>

            <div>
              <div className="bg-white p-8 rounded border border-slate-200">
                <h2 className="text-2xl font-serif font-bold mb-6">Teaching Notes Requests</h2>
                {teachingNoteRequests.length === 0 ? (
                  <p className="text-slate-600">No teaching note requests yet</p>
                ) : (
                  <div className="space-y-4">
                    {teachingNoteRequests.map((request) => (
                      <div key={request.id} className="p-4 border border-slate-200 rounded">
                        <div className="font-semibold text-slate-900 mb-2">{request.case_title}</div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <div><span className="font-medium">Requested by:</span> {request.user_name}</div>
                          <div><span className="font-medium">Email:</span> {request.user_email}</div>
                          <div><span className="font-medium">Institution:</span> {request.institution || 'Not provided'}</div>
                          <div><span className="font-medium">Status:</span> 
                            <span className={`ml-2 px-2 py-1 rounded text-xs ${
                              request.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {request.status}
                            </span>
                          </div>
                          <div><span className="font-medium">Requested:</span> {new Date(request.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
